import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { motion } from "framer-motion";
import {
  Heart, PaperPlaneTilt, HandHeart, Trophy,
  SunDim, Leaf, FireSimple, Wind, Drop, Moon, Star, Check,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_OPTIONS = [
  { id: "happy", label: "Happy", icon: SunDim, color: "#F4A261" },
  { id: "calm", label: "Calm", icon: Leaf, color: "#84A98C" },
  { id: "stressed", label: "Stressed", icon: FireSimple, color: "#E76F51" },
  { id: "anxious", label: "Anxious", icon: Wind, color: "#CDB4DB" },
  { id: "sad", label: "Sad", icon: Drop, color: "#457B9D" },
  { id: "tired", label: "Tired", icon: Moon, color: "#A8DADC" },
  { id: "grateful", label: "Grateful", icon: Heart, color: "#2D6A4F" },
  { id: "hopeful", label: "Hopeful", icon: Star, color: "#FFD166" },
];

const PASTEL_BG = ["#FAF7F2", "#f0ebe3", "#eaf2ec", "#f3eff6", "#fef4ee"];

export default function CommunityWallPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [affirmations, setAffirmations] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [orgChallenges, setOrgChallenges] = useState([]);
  const [tab, setTab] = useState("wall"); // wall | affirmations | challenge
  const [mood, setMood] = useState("");
  const [message, setMessage] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/community/posts`, { withCredentials: true }),
      axios.get(`${API}/community/affirmations`, { withCredentials: true }),
      axios.get(`${API}/community/challenge`, { withCredentials: true }),
    ]).then(([postsRes, affRes, chalRes]) => {
      setPosts(postsRes.data);
      setAffirmations(affRes.data);
      setChallenge(chalRes.data);
    }).catch(console.error);

    // Load org challenges if user is in an org
    axios.get(`${API}/challenges/active`, { withCredentials: true })
      .then((res) => setOrgChallenges(res.data))
      .catch(() => {});
  }, []);

  const handlePost = async () => {
    if (!mood || !message.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(
        `${API}/community/posts`,
        { mood, message: message.trim(), is_anonymous: true },
        { withCredentials: true }
      );
      setPosts((prev) => [res.data, ...prev]);
      setMood("");
      setMessage("");
    } catch (err) {
      console.error("Post failed:", err);
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const res = await axios.post(`${API}/community/posts/${postId}/like`, {}, { withCredentials: true });
      setPosts((prev) => prev.map((p) => (p.id === postId ? res.data : p)));
    } catch {}
  };

  return (
    <div data-testid="community-wall-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#2D6A4F] mb-2">Community</p>
        <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2421] tracking-tight">
          The Collective Reset
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: "wall", label: "Mood Wall", icon: HandHeart },
          { key: "affirmations", label: "Affirmations", icon: Heart },
          { key: "challenge", label: "7-Day Challenge", icon: Trophy },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`community-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              tab === t.key
                ? "bg-[#2D6A4F] text-white shadow-md"
                : "bg-white text-[#78857F] hover:bg-[#2D6A4F]/5 border border-[#E1D9CD]"
            }`}
          >
            <t.icon size={16} weight="duotone" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Wall Tab */}
      {tab === "wall" && (
        <div>
          {/* Post Form */}
          <div className="card-neuro p-6 mb-6" data-testid="community-post-form">
            <p className="text-sm font-medium text-[#1A2421] mb-3">Share how you're feeling (anonymously)</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {MOOD_OPTIONS.map((m) => (
                <button
                  key={m.id}
                  data-testid={`post-mood-${m.id}`}
                  onClick={() => setMood(m.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    mood === m.id
                      ? "text-white shadow-sm"
                      : "bg-white border border-[#E1D9CD] text-[#78857F] hover:border-current"
                  }`}
                  style={mood === m.id ? { backgroundColor: m.color } : {}}
                >
                  <m.icon size={14} weight="duotone" />
                  {m.label}
                </button>
              ))}
            </div>
            <Textarea
              data-testid="community-post-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's on your mind?"
              className="border-[#E1D9CD] bg-[#FAF7F2] rounded-xl resize-none mb-3 min-h-[60px] focus:border-[#2D6A4F]"
            />
            <Button
              onClick={handlePost}
              disabled={posting || !mood || !message.trim()}
              data-testid="community-post-submit"
              className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4936] text-white"
            >
              <PaperPlaneTilt size={16} weight="fill" className="mr-1.5" />
              {posting ? "Sharing..." : "Share Anonymously"}
            </Button>
          </div>

          {/* Posts Masonry */}
          <div className="masonry-grid">
            {posts.map((post, i) => {
              const moodOpt = MOOD_OPTIONS.find((m) => m.id === post.mood);
              return (
                <motion.div
                  key={post.id}
                  data-testid={`community-post-${post.id}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="rounded-2xl p-5 border border-black/5"
                  style={{ backgroundColor: PASTEL_BG[i % PASTEL_BG.length] }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {moodOpt && (
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
                        style={{ backgroundColor: moodOpt.color }}
                      >
                        {moodOpt.label}
                      </span>
                    )}
                    <span className="text-[10px] text-[#78857F]">
                      {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  </div>
                  <p className="text-sm text-[#1A2421] mb-3 leading-relaxed">{post.message}</p>
                  <button
                    onClick={() => handleLike(post.id)}
                    data-testid={`like-post-${post.id}`}
                    className="flex items-center gap-1 text-xs text-[#78857F] hover:text-[#E76F51] transition-colors"
                  >
                    <Heart size={14} weight="duotone" />
                    {post.likes || 0}
                  </button>
                </motion.div>
              );
            })}
          </div>
          {posts.length === 0 && (
            <p className="text-center text-sm text-[#78857F] py-12">
              Be the first to share your mood with the community.
            </p>
          )}
        </div>
      )}

      {/* Affirmations Tab */}
      {tab === "affirmations" && (
        <div className="masonry-grid" data-testid="affirmations-wall">
          {affirmations.map((aff, i) => (
            <motion.div
              key={aff.id}
              data-testid={`affirmation-${aff.id}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.04 }}
              className="rounded-2xl p-6 border border-black/5"
              style={{ backgroundColor: PASTEL_BG[i % PASTEL_BG.length] }}
            >
              <HandHeart size={20} weight="duotone" className="text-[#84A98C] mb-2" />
              <p className="text-sm text-[#1A2421] italic leading-relaxed">"{aff.text}"</p>
              <span className="text-[10px] text-[#78857F] uppercase tracking-wider mt-2 block">
                {aff.category}
              </span>
            </motion.div>
          ))}
        </div>
      )}

      {/* Challenge Tab */}
      {tab === "challenge" && (
        <div data-testid="challenge-tracker">
          {challenge && (
            <div className="max-w-md mx-auto">
              <div className="card-neuro p-8 text-center">
                <Trophy size={40} weight="duotone" className="text-[#F4A261] mx-auto mb-4" />
                <h3 className="font-['Outfit'] text-xl font-medium text-[#1A2421] mb-2">
                  7-Day Mood Check-in Challenge
                </h3>
                <p className="text-sm text-[#78857F] mb-6">
                  Check in with your mood every day for 7 days to build awareness.
                </p>
                <div className="flex justify-center gap-3 mb-6">
                  {Array.from({ length: 7 }, (_, i) => {
                    const isCompleted = i < challenge.days_completed;
                    return (
                      <motion.div
                        key={i}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: i * 0.06, type: "spring" }}
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ${
                          isCompleted ? "bg-[#2D6A4F] text-white" : "bg-[#EAE4D9] text-[#78857F]"
                        }`}
                        data-testid={`challenge-day-${i + 1}`}
                      >
                        {isCompleted ? <Check size={18} weight="bold" /> : i + 1}
                      </motion.div>
                    );
                  })}
                </div>
                <p className="text-sm text-[#2D6A4F] font-medium">
                  {challenge.days_completed}/7 days completed
                </p>
                {challenge.days_completed >= 7 && (
                  <p className="text-sm text-[#F4A261] font-medium mt-2">
                    Challenge complete! You're a wellness champion.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Org Wellness Challenges */}
          {orgChallenges.length > 0 && (
            <div className="mt-6 space-y-4" data-testid="org-challenges-display">
              <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421] text-center">
                Team Challenges
              </h3>
              {orgChallenges.map((ch) => {
                const pct = Math.min(100, Math.round((ch.my_progress / ch.goal) * 100));
                const typeLabels = { daily_checkin: "Check-ins", sessions: "Sessions", journal: "Journals", custom: "Activities" };
                return (
                  <div key={ch.id} className="card-neuro p-6 max-w-md mx-auto" data-testid={`org-challenge-${ch.id}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <Trophy size={24} weight="duotone" className="text-[#F4A261]" />
                      <div>
                        <h4 className="font-['Outfit'] text-base font-medium text-[#1A2421]">{ch.title}</h4>
                        <p className="text-[10px] text-[#78857F]">
                          {typeLabels[ch.challenge_type] || ch.challenge_type} · {ch.days_left} days left
                        </p>
                      </div>
                    </div>
                    {ch.description && <p className="text-xs text-[#78857F] mb-3">{ch.description}</p>}
                    <div className="mb-2">
                      <div className="flex justify-between mb-1">
                        <span className="text-xs font-medium text-[#1A2421]">{ch.my_progress} / {ch.goal}</span>
                        <span className="text-xs text-[#78857F]">{pct}%</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-[#EAE4D9] overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: ch.is_completed ? "#2D6A4F" : "#F4A261" }}
                        />
                      </div>
                    </div>
                    {ch.is_completed && (
                      <div className="flex items-center gap-2 mt-3">
                        <Check size={16} weight="bold" className="text-[#2D6A4F]" />
                        <p className="text-sm text-[#2D6A4F] font-medium">Challenge complete!</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
